import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  MapPinIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { thingsAPI } from '../services/api';
import { getImageUrl } from '../config/runtime';
import { normalizeTaxonomyHierarchyEntries } from '../utils/taxonomy';
import BBoxOverlay from '../components/BBoxOverlay';

const confidenceTone = (value) => {
  if (value == null) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-200';
  if (value >= 0.75) return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  if (value >= 0.45) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
  return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
};

const taxConfidenceTone = (level) => {
  if (!level) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-200';
  const normalized = level.toLowerCase();
  if (normalized === 'high') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  if (normalized === 'medium') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
  return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
};

const SectionCard = ({ title, subtitle, children, action, className = '' }) => (
  <section className={`bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 ${className}`}>
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const formatDate = (value) => {
  if (!value) return null;
  try {
    const d = new Date(value);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return value;
  }
};

const CommunityDetectionDetailPage = () => {
  const { detectionId } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const location = useLocation();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['community-detection', detectionId],
    enabled: Boolean(detectionId),
    queryFn: async () => {
      const response = await thingsAPI.getDetection(detectionId);
      return response.data;
    },
  });

  const detection = data;
  const imageUrl = detection?.sample_images?.[0] ? getImageUrl(detection.sample_images[0]) : null;
  const captureTimestamp = formatDate(detection?.last_seen_at);
  const latLng = detection?.lat != null && detection?.lng != null
    ? `${detection.lat.toFixed(4)}, ${detection.lng.toFixed(4)}`
    : null;
  const taxonomyEntries = useMemo(() => {
    return normalizeTaxonomyHierarchyEntries(detection?.taxonomic_classification?.hierarchy);
  }, [detection?.taxonomic_classification?.hierarchy]);

  const handleCopyCoords = async () => {
    if (!latLng) return;
    try {
      await navigator.clipboard.writeText(latLng);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const goBackToCommunity = () => {
    navigate('/discover', {
      state: {
        transitionDirection: 'back',
        previousPath: location.pathname,
      },
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="max-w-6xl mx-auto px-4 py-12 space-y-6">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
          <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
            <div className="aspect-video bg-gray-200 dark:bg-gray-800 rounded-3xl animate-pulse" />
            <div className="space-y-4">
              {[...Array(3)].map((_, idx) => (
                <div key={idx} className="h-32 bg-gray-200 dark:bg-gray-800 rounded-3xl animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (isError || !detection) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-4xl mb-4">🛰️</div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Detection not available</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {isError ? 'We could not load this detection right now.' : 'This detection may have been removed.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={refetch}
              className="px-5 py-2.5 rounded-full bg-gray-900 text-white text-sm font-semibold shadow-sm"
            >
              Try again
            </button>
            <button
              onClick={goBackToCommunity}
              className="px-5 py-2.5 rounded-full border border-gray-300 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              Back to Discover
            </button>
          </div>
        </div>
      );
    }

    const googleMapsUrl = latLng ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(latLng)}` : null;
    const rawCommon = detection?.taxonomic_classification?.common_names || detection?.taxonomic_classification?.vernacular_names || [];
    const commonNames = Array.isArray(rawCommon) ? rawCommon : (rawCommon ? [rawCommon] : []);
    const confusionSpecies = Array.isArray(detection?.common_confusion_species) ? detection.common_confusion_species : [];

    return (
      <div className="max-w-6xl mx-auto px-4 py-6 lg:py-12 space-y-6 mb-20">
        {/* Navigation */}
        <button
          onClick={goBackToCommunity}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Discoveries
        </button>

        {/* Hero Section: Image + Primary Info */}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)]">
          {/* Image Card */}
          <section className="bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div className="relative w-full bg-gray-100 dark:bg-gray-800">
              {imageUrl ? (
                (detection?.bbox || (Array.isArray(detection?.bboxes) && detection.bboxes?.length > 0)) ? (
                  <BBoxOverlay
                    src={imageUrl}
                    bbox={detection.bbox}
                    bboxes={detection.bboxes}
                    alt={detection.canonical_name || 'Detection'}
                    className="w-full"
                    fillMode="contain"
                    borderColor="rgba(16,185,129,0.9)"
                    overlayClassName="rounded-lg"
                  />
                ) : (
                  <img 
                    src={imageUrl} 
                    alt={detection.canonical_name || 'Detection'} 
                    className="w-full h-auto block"
                  />
                )
              ) : (
                <div className="w-full h-[420px] flex items-center justify-center text-5xl">🔍</div>
              )}
            </div>
            <div className="p-6">
              <div className="flex flex-wrap items-start gap-2 mb-3">
                {detection.category && (
                  <span className="text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                    {detection.category.replace(/_/g, ' ')}
                  </span>
                )}
                <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${confidenceTone(detection.confidence)}`}>
                  {detection.confidence != null ? `${Math.round(detection.confidence * 100)}% confidence` : 'Confidence n/a'}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                {detection.canonical_name || detection.label || 'Unknown discovery'}
              </h1>
              {detection.description && (
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {detection.description}
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(AI generated)</span>
                </p>
              )}
            </div>
          </section>

          {/* Quick Info Sidebar */}
          <div className="space-y-4">
            <SectionCard title="Details" className="p-5">
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
                    <ClockIcon className="w-3.5 h-3.5" />
                    Captured
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {captureTimestamp || 'Not available'}
                  </div>
                </div>
                
                {detection.user_display || detection.username ? (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                      Observer
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {detection.user_display || detection.username || 'Unknown'}
                        </div>
                        {detection.username && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">@{detection.username}</div>
                        )}
                      </div>
                      {detection.username && (
                        <Link
                          to={`/users/${detection.username}`}
                          className="text-xs font-medium text-green-600 dark:text-green-400 hover:underline whitespace-nowrap"
                        >
                          View →
                        </Link>
                      )}
                    </div>
                  </div>
                ) : null}

                {detection.project_name && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                      Project
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {detection.project_name}
                      </div>
                      {detection.project_id && (
                        <Link
                          to={`/projects/${detection.project_id}`}
                          className="text-xs font-medium text-green-600 dark:text-green-400 hover:underline whitespace-nowrap"
                        >
                          View →
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {latLng && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
                      <MapPinIcon className="w-3.5 h-3.5" />
                      Location
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                        {latLng}
                      </div>
                      <div className="flex items-center gap-2">
                        {googleMapsUrl && (
                          <a
                            href={googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-green-600 dark:text-green-400 hover:underline whitespace-nowrap"
                            title="Open in Google Maps"
                          >
                            Map →
                          </a>
                        )}
                        <button
                          onClick={handleCopyCoords}
                          className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                          title="Copy coordinates"
                        >
                          {copied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>

            {(detection.identification_notes || detection.requires_expert_review) && (
              <SectionCard title="Notes" className="p-5">
                {detection.identification_notes && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    {detection.identification_notes}
                  </p>
                )}
                {detection.requires_expert_review && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    <span className="text-xs font-semibold">Expert review required</span>
                  </div>
                )}
              </SectionCard>
            )}
          </div>
        </div>

        {/* Taxonomic Classification */}
        {(taxonomyEntries.length > 0 || commonNames.length > 0 || confusionSpecies.length > 0 || detection.taxonomic_classification?.domain) && (
          <SectionCard title="Taxonomic Classification" subtitle="Scientific classification hierarchy">
            {detection.taxonomic_classification?.domain && (
              <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <GlobeAltIcon className="w-4 h-4" />
                  <span className="font-medium">Domain:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{detection.taxonomic_classification.domain}</span>
                </div>
                {detection.taxonomic_confidence && (
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${taxConfidenceTone(detection.taxonomic_confidence)}`}>
                    {detection.taxonomic_confidence} confidence
                  </span>
                )}
              </div>
            )}
            
            {taxonomyEntries.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-4">
                {taxonomyEntries.map(([key, value], idx) => (
                  <div key={`${key}-${idx}`} className="flex flex-col">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
                      {key || `Level ${idx + 1}`}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</span>
                  </div>
                ))}
              </div>
            )}
            
            {commonNames.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Common Names
                </div>
                <div className="flex flex-wrap gap-2">
                  {commonNames.map((name) => (
                    <span key={name} className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {confusionSpecies.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Similar Species
                </div>
                <div className="flex flex-wrap gap-2">
                  {confusionSpecies.map((name) => (
                    <span key={name} className="px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      {renderContent()}
    </div>
  );
};

export default CommunityDetectionDetailPage;

